import { useEffect, useState } from 'react'
import { TodoCategory } from '@hewey/shared'
import { colorFor } from '@utils/colors.ts'
import { Dialog } from '@components/Dialog.tsx'
import styles from './CategoryPicker.module.css'

type Props = {
	open: boolean
	current: TodoCategory | null
	existing: TodoCategory[]
	onPick: (category: TodoCategory | null) => void
	onClose: () => void
}

export const CategoryPicker = ({
	open,
	current,
	existing,
	onPick,
	onClose,
}: Props) => {
	const [draft, setDraft] = useState('')
	const [error, setError] = useState<string | null>(null)

	useEffect(() => {
		if (open) {
			setDraft('')
			setError(null)
		}
	}, [open])

	const submit = (e: React.FormEvent) => {
		e.preventDefault()
		const trimmed = draft.trim()
		if (trimmed.length === 0) return
		const match = existing.find(
			(c) => c.toLowerCase() === trimmed.toLowerCase(),
		)
		if (match) {
			onPick(match)
			return
		}
		const parsed = TodoCategory.safeParse(trimmed)
		if (!parsed.success) {
			setError(parsed.error.issues[0]?.message ?? 'Invalid category')
			return
		}
		onPick(parsed.data)
	}

	const sorted = [...existing].sort((a, b) =>
		a.toLowerCase().localeCompare(b.toLowerCase()),
	)

	return (
		<Dialog open={open} onClose={onClose}>
			<div className={styles.panel}>
				<h2 className={styles.heading}>Move to list</h2>

				<ul className={styles.list}>
					<li>
						<button
							type="button"
							className={`${styles.item} ${current === null ? styles.selected : ''}`.trim()}
							onClick={() => onPick(null)}
						>
							<span
								className={styles.dot}
								style={{ background: 'var(--ink-muted)' }}
								aria-hidden="true"
							/>
							general
						</button>
					</li>
					{sorted.map((cat) => (
						<li key={cat}>
							<button
								type="button"
								className={`${styles.item} ${current === cat ? styles.selected : ''}`.trim()}
								onClick={() => onPick(cat)}
							>
								<span
									className={styles.dot}
									style={{ background: colorFor(cat) }}
									aria-hidden="true"
								/>
								{cat}
							</button>
						</li>
					))}
				</ul>

				<form className={styles.form} onSubmit={submit}>
					<input
						className={styles.input}
						value={draft}
						maxLength={24}
						placeholder="New category…"
						onChange={(e) => {
							setDraft(e.target.value)
							setError(null)
						}}
						aria-label="New category name"
					/>
					<button
						type="submit"
						className={styles.add}
						disabled={draft.trim().length === 0}
					>
						Add
					</button>
				</form>
				{error && <p className={styles.error}>{error}</p>}
			</div>
		</Dialog>
	)
}
